import { useEffect, useState } from 'react';
import { Badge, Button, Dialog, Flex } from '@radix-ui/themes';
import { api, getDbMode, setDbMode } from '../lib/api';

/**
 * Database mode toggle component for admin users
 * Shows PROD/SEED badge that can be clicked to switch databases
 */
export default function DatabaseModeToggle({ isAdmin = false }) {
  const [mode, setMode] = useState(getDbMode());
  const [seedAvailable, setSeedAvailable] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  // Check if seed database is available
  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/db-mode');
        if (mounted) {
          setSeedAvailable(res.data?.seedAvailable || false);
        }
      } catch (err) {
        console.warn('Failed to check db mode availability:', err);
      }
    })();
    return () => { mounted = false; };
  }, [isAdmin]);

  // Listen for mode changes from other tabs/components
  useEffect(() => {
    const handleChange = (e) => setMode(e.detail);
    window.addEventListener('db-mode-change', handleChange);
    return () => window.removeEventListener('db-mode-change', handleChange);
  }, []);

  const handleToggle = () => {
    const newMode = mode === 'production' ? 'seed' : 'production';
    setPendingMode(newMode);
    setConfirmOpen(true);
  };

  const confirmChange = () => {
    if (pendingMode) {
      setDbMode(pendingMode);
      setMode(pendingMode);
      window.location.reload();
    }
    setConfirmOpen(false);
  };

  const isSeed = mode === 'seed';

  // Don't show if not admin or seed not available
  if (!isAdmin || !seedAvailable) {
    return null;
  }

  return (
    <>
      <Badge
        color={isSeed ? 'orange' : 'blue'}
        variant="soft"
        size="1"
        style={{ cursor: 'pointer' }}
        onClick={handleToggle}
        title={isSeed ? 'Using SEED database (test data)' : 'Using PRODUCTION database'}
      >
        {isSeed ? '🧪 SEED' : '🗄️ PROD'}
      </Badge>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content maxWidth="400px">
          <Dialog.Title>
            Switch to {pendingMode === 'seed' ? 'Seed' : 'Production'} Database?
          </Dialog.Title>
          <Dialog.Description size="2" mb="4">
            {pendingMode === 'seed'
              ? "You will view TEST data from the seed database. Changes here won't affect production."
              : 'You will view REAL production data. Be careful with any changes.'}
          </Dialog.Description>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button
              color={pendingMode === 'seed' ? 'orange' : 'blue'}
              onClick={confirmChange}
            >
              Switch to {pendingMode === 'seed' ? 'Seed' : 'Production'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
